<script setup lang="ts">
  import { findAccountSchema } from "~~/validators/auth";
  import type { FormSubmitEvent } from "#ui/types";
  import constants from "~~/constants";
  import type { z } from "zod";

  const emits = defineEmits<{
    (e: "resetLinkSent", email: string): void;
  }>();
  const schema = findAccountSchema();
  type Schema = z.output<typeof schema>;

  const state = reactive<Partial<Schema>>({
    email: "",
  });

  const isSubmitting = ref(false);
  const errorMessage = ref("");

  const { mainBaseURL } = useRuntimeConfig().public;
  const { post } = apiRepository(mainBaseURL);

  async function onSubmit(event: FormSubmitEvent<Schema>) {
    isSubmitting.value = true;

    const { success, status, message } = await post(
      "/auth/forgot-password",
      { ...event.data, isEnumerator: true },
    );

    isSubmitting.value = false;

    if (success) {
      emits("resetLinkSent", event.data.email);
      return;
    }

    if (status == constants.STATUS_NOT_FOUND) {
      errorMessage.value = message;
    } else {
      throw createError({
        statusCode: status,
        statusMessage: message,
      });
    }
  }
</script>

<template>
  <UForm
    :schema="schema"
    :state="state"
    @submit="onSubmit"
    :validateOn="['change']"
    class="space-y-6"
  >
    <UFormField :label="$t('labels.email')" name="email">
      <UInput v-model="state.email" placeholder="Enter email" />
    </UFormField>
    <div class="space-y-3">
      <UButton type="submit" :loading="isSubmitting">{{
        $t("actions.sendResetLink")
      }}</UButton>
      <UButton
        to="/signin"
        class="text-primary-500 bg-transparent p-0 text-xs font-medium hover:bg-transparent hover:underline"
        icon="heroicons-arrow-left-circle-solid"
      >
        {{ $t("labels.back-to-signin") }}
      </UButton>
    </div>
  </UForm>
</template>

<style scoped></style>
